import { __rest } from "tslib";
import lodashGet from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { wait } from '@/api/util';
import { inWindow } from '@/config';
import { transformNewUnitList, transformNewContactList, transformNewContactListForOrgIds, aggregateNewContacts, createEnterpriseRootUnit, } from './transform/enterprise_new';
export class ContactServerEnterprise {
    constructor(ctx, common) {
        this.idPathMap = new Map();
        this.enterpriseFullCount = 0;
        this.ctx = ctx;
        this.common = common;
    }
    async doSyncCoreContactData(options) {
        const { subDomain, enableFastCleanInvalidContact } = options, restOptions = __rest(options, ["subDomain", "enableFastCleanInvalidContact"]);
        const url = this.ctx.systemApi.getUrl('coreEnterpriseContactList');
        const domain = typeof subDomain === 'string' && subDomain.length > 0 ? subDomain : this.common.getDomain(options._account);
        try {
            const res = await this.common.excuteContactServerWithRetry(Object.assign({ url, params: { domain } }, restOptions));
            const { contactVOList, orgId: enterpriseId, lastUpdateTime = Date.now() - 1000 * 5, incTimeInternal } = res;
            let { iconPrefix = '' } = res;
            const orgpathlist = await this.subscribleOrgPathlistRawdata(Object.assign({ enterpriseId }, restOptions));
            this.ctx.contactUtil.setContactSyncIntervalSeq(incTimeInternal);
            let partialData = contactVOList.splice(0, 20);
            let realContactList = partialData;
            if (lodashGet(partialData, 'length', 0) === 0 && !subDomain && this.ctx.systemApi.getIsMainAccount(options._account)) {
                realContactList = await this.fetchSelfInfo(options._account);
                iconPrefix = '';
            }
            await this.ctx.contactDB.coreContactAllInto(Object.assign({ list: realContactList, orgpathlist }, restOptions), {
                enterpriseId,
                iconPrefix,
                enableFastCleanInvalidContact,
            });
            await this.ctx.contactUtil.setStoreContactSyncPageInfo(Object.assign({ from: 'enterprise', domain: domain, step: 'core', done: true, source: 'unknown', lastUpdateTime, coreCount: partialData.length }, restOptions));
            return {
                from: 'enterprise',
                success: true,
                count: lodashGet(partialData, 'length', 0),
            };
        }
        catch (ex) {
            return {
                from: 'enterprise',
                success: false,
                message: lodashGet(ex, 'message', `${ex}`),
                count: 0,
            };
        }
    }
    async subscribleOrgPathlistRawdata(options) {
        const { enterpriseId } = options, restOptions = __rest(options, ["enterpriseId"]);
        inWindow() && window.bridgeApi.master.forbiddenBridgeOnce();
        const count = await this.ctx.contactDB.getTableCount({
            tableName: this.ctx.contactUtil.orgpathlistTable,
            dbName: this.ctx.contactUtil.contactSearchDbName,
            enterpriseId,
            _account: options._account,
        });
        if (!count) {
            await this.ctx.eventHub.waitForOrgPathlistReady(options._account, {
                filter: e => {
                    const currentEnterpriseId = lodashGet(e, 'eventData.enterpriseId', 0);
                    console.log('[contact_server]subscribleOrgPathlistRawdata', e.eventData);
                    return enterpriseId === currentEnterpriseId;
                },
            });
        }
        return this.ctx.contactDB.getTotalLokiData(Object.assign({ tableName: this.ctx.contactUtil.orgpathlistTable, enableProxy: false }, restOptions));
    }
    async doSyncCoreOrgData(options) {
        const url = this.ctx.systemApi.getUrl('coreEnterpriseOrgList');
        const { subDomain, enableFastCleanInvalidContact } = options, restOptions = __rest(options, ["subDomain", "enableFastCleanInvalidContact"]);
        const domain = typeof subDomain === 'string' && subDomain.length > 0 ? subDomain : this.common.getDomain(options._account);
        try {
            const res = await this.common.excuteContactServerWithRetry(Object.assign({ url, params: { domain } }, restOptions));
            const { unitVOList, orgId: enterpriseId, lastUpdateTime, incTimeInternal } = res;
            this.ctx.contactUtil.setContactSyncIntervalSeq(incTimeInternal);
            let realUnitList = unitVOList;
            if (lodashGet(unitVOList, 'length', 0) === 0 && !subDomain) {
                const mainCompanyId = this.ctx.systemApi.getCurrentCompanyId(options._account);
                realUnitList = [
                    {
                        oriUnitId: `org_${mainCompanyId}`,
                        parentUnitId: '',
                        rank: 0,
                        type: 99,
                        unitId: `org_${mainCompanyId}`,
                        unitName: lodashGet(this.ctx.systemApi.getCurrentUser(options._account), 'company', ''),
                        unitRank: 0,
                    },
                ];
            }
            await this.ctx.contactDB.coreOrgAllInto(Object.assign({ list: realUnitList }, restOptions), {
                visibleCode: res.statusCode === 107000 ? 7 : res.statusCode,
                enterpriseId,
                enableFastCleanInvalidContact,
            });
            await this.ctx.contactUtil.setStoreContactSyncPageInfo(Object.assign({ from: 'org', domain: domain, step: 'core', done: true, lastUpdateTime, source: 'unknown' }, restOptions));
            return {
                success: false,
                from: 'org',
                count: lodashGet(unitVOList, 'length', 0),
            };
        }
        catch (ex) {
            return {
                success: false,
                from: 'org',
                count: 0,
            };
        }
    }
    async fetchSelfInfo(_account) {
        var _a;
        const url = this.ctx.systemApi.getUrl('getAccountInfo');
        const sid = ((_a = this.ctx.systemApi.getCurrentUser(_account)) === null || _a === void 0 ? void 0 : _a.sessionId) || '';
        const accountRes = await this.ctx.http.get(url, { sid, needUnitNamePath: true }, { _account });
        if (!lodashGet(accountRes, 'data.data.email.length', 0)) {
            return [];
        }
        const accountInfoData = accountRes.data.data;
        return [
            {
                domain: '',
                accountId: accountInfoData.qiyeAccountId,
                accountName: accountInfoData.email,
                nickName: accountInfoData.nickName,
                type: 2,
                mediumIconUrl: lodashGet(accountInfoData, 'iconVO.mediumUrl', ''),
                smallIconUrl: lodashGet(accountInfoData, 'iconVO.mediumUrl', ''),
                email: accountInfoData.email,
                orgId: Number(accountInfoData.qiyeAccountId),
                rankList: [],
            },
        ];
    }
    async syncEnterpriseContactList(options) {
        const from = 'enterprise';
        const { params: _params, subDomain, isMainAccount, _account } = options;
        let params;
        if (_params) {
            params = _params;
        }
        else {
            params = await this.common.getSyncServerParams({
                from,
                force: true,
                subDomain,
                isMainAccount,
                _account,
            });
        }
        const url = this.ctx.systemApi.getUrl(this.common.getServerUrl(from, Object.assign(Object.assign({}, params), { force: true, needUnitNamePath: true })));
        try {
            const { contactVOList, source, pageIndex, pageSize, totalPage, lastUpdateTime, incTimeInternal } = await this.common.excuteContactServerWithRetry({
                url,
                params,
                isMainAccount,
                _account,
            }, {});
            let partialData = contactVOList.splice(0, 20);
            this.enterpriseFullCount += partialData.length || 0;
            this.ctx.contactUtil.setContactSyncIntervalSeq(incTimeInternal);
            let isDone = false;
            let enableUseNewtest = true;
            if (!Array.isArray(partialData) || !partialData.length) {
                isDone = true;
            }
            else {
                const insertResult = await this.ctx.contactDB.enterpriseAllInto({
                    list: partialData,
                    force: true,
                    isMainAccount,
                    _account,
                });
                enableUseNewtest = !Object.keys(lodashGet(insertResult, '[0].insertCountMap', {})).length;
            }
            if (!isDone) {
                isDone = !!this.isDone4Enterprise(partialData, {
                    isAll: true,
                    source,
                    pageIndex,
                    totalPage,
                    pageSize,
                });
            }
            if (isDone) {
                this.ctx.contactUtil.setStoreContactSyncPageInfo({
                    from,
                    domain: params.domain || this.common.getDomain(options._account),
                    lastUpdateTime,
                    step: 'all',
                    source: 'unknown',
                    pageIndex: pageSize,
                    done: true,
                    totalPage,
                    isMainAccount,
                    _account,
                }, enableUseNewtest);
                this.recordEnterpriseSyncCount({
                    isForce: true,
                    count: this.enterpriseFullCount,
                    isMainAccount,
                    _account,
                });
                return {
                    count: lodashGet(partialData, 'length', 0),
                    from: 'enterprise',
                    data: [],
                    success: true,
                };
            }
            const nextRequestParam = cloneDeep(params);
            if (source === 'lingxi') {
                nextRequestParam.source = 'lingxi';
                nextRequestParam.lastMaxId = lodashGet(partialData, `[${partialData.length - 1}].qiyeAccountId`, '');
                this.ctx.contactUtil.setStoreContactSyncPageInfo({
                    from,
                    domain: params.domain,
                    source: 'lingxi',
                    lastMaxId: params.lastMaxId,
                    step: 'all',
                    done: false,
                    _account,
                    isMainAccount,
                });
            }
            else {
                nextRequestParam.source = source;
                nextRequestParam.page_index = pageIndex + 1;
                this.ctx.contactUtil.setStoreContactSyncPageInfo({
                    from,
                    domain: params.domain,
                    source,
                    pageIndex: pageSize,
                    done: false,
                    totalPage,
                    step: 'all',
                    _account,
                    isMainAccount,
                });
            }
            await wait(2000);
            return this.syncEnterpriseContactList({
                force: true,
                subDomain,
                params: nextRequestParam,
                isMainAccount,
                _account,
            });
        }
        catch (ex) {
            return {
                from: 'enterprise',
                success: false,
                data: [],
                count: 0,
                message: ex instanceof Error ? ex.message : `${ex}`,
            };
        }
    }
    async recordEnterpriseSyncCount(options) {
        const { isForce, count, _account, isMainAccount } = options;
        if (!isForce) {
            return;
        }
        this.enterpriseFullCount = 0;
        await wait(10);
        const pageInfo = await this.ctx.contactUtil.getStoreContactSyncPageInfo({
            from: 'enterprise',
            domain: this.common.getDomain(),
            _account,
            isMainAccount,
        });
        const coreCount = lodashGet(pageInfo, 'coreCount', count);
        this.ctx.dataTrackerApi.track('pc_enterprisefull_syncdone', {
            _account: options._account,
            count,
            coreCount,
            percent: Math.round((coreCount * 100) / count),
            enableTrackInBg: true,
        });
    }
    isDone4Enterprise(list, options) {
        const { isAll: isSyncAll, source = 'unknown' } = options;
        if (source === 'lingxi' && isSyncAll) {
            const { pageSize = 5000 } = options;
            return list.length === 0 || (typeof pageSize === 'number' && list.length < pageSize);
        }
        const { pageIndex = 0, totalPage = 0 } = options;
        return pageIndex && totalPage && pageIndex === totalPage;
    }
    async syncSelfYunxin(params) {
        var _a, _b, _c, _d;
        const { _account } = params;
        const isMainAccount = !_account || ((_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id) === _account;
        const hasYunxin = !!((_c = (_b = this.ctx.systemApi.getCurrentUser()) === null || _b === void 0 ? void 0 : _b.contact) === null || _c === void 0 ? void 0 : _c.contactInfo.find(item => item.contactItemType === 'yunxin'));
        if (!isMainAccount || hasYunxin) {
            return;
        }
        const url = this.ctx.systemApi.getUrl('getAccountInfo');
        const sid = ((_d = this.ctx.systemApi.getCurrentUser(_account)) === null || _d === void 0 ? void 0 : _d.sessionId) || '';
        const accountRes = await this.ctx.http.get(url, { sid, needUnitNamePath: false }, { _account });
        if (lodashGet(accountRes, 'data.data.yunxinAccountId.length', 0) === 0) {
            return;
        }
        const { yunxinAccountId, qiyeAccountId, orgId } = accountRes.data.data;
        const { util } = await import('@/api/util');
        await this.ctx.contactDB.intoTable({
            tableName: this.ctx.contactUtil.contactItemTable,
            list: [
                {
                    id: util.getUnique(qiyeAccountId, 'yunxin', yunxinAccountId),
                    contactId: qiyeAccountId,
                    contactItemRefer: '',
                    contactItemType: 'yunxin',
                    contactItemVal: yunxinAccountId,
                    enterpriseId: orgId,
                    isDefault: 0,
                    type: 'enterprise',
                    _lastUpdateTime: Date.now(),
                },
            ],
            _account,
        });
    }
    async syncOrgList(options) {
        const { force = false, subDomain, isMainAccount, _account } = options;
        const from = 'org';
        try {
            const params = await this.common.getSyncServerParams({
                from,
                force,
                subDomain,
                isMainAccount,
                _account,
            });
            const url = this.ctx.systemApi.getUrl(this.common.getServerUrl(from, params));
            const { unitVOList: list = [], lastUpdateTime } = await this.common.excuteContactServerWithRetry({
                url,
                params,
                isMainAccount,
                _account,
            }, force ? {} : { correctCodes: [0, 106000, 107000] });
            if (Array.isArray(list) && list.length) {
                await this.ctx.contactDB.orgAllInto({
                    list,
                    isMainAccount,
                    _account,
                });
            }
            this.ctx.contactUtil.setStoreContactSyncPageInfo({
                from,
                domain: this.common.getDomain() || '',
                step: force ? 'all' : 'increase',
                done: true,
                source: 'unknown',
                lastUpdateTime,
                isMainAccount,
                _account,
            });
            return {
                success: true,
                count: lodashGet(list, 'length', 0),
                data: [],
                from,
            };
        }
        catch (ex) {
            return {
                from,
                success: false,
                data: [],
                message: ex instanceof Error ? ex.message : `${ex}`,
            };
        }
    }
    async getColleagueList() {
        var _a;
        const { success, data, error } = await this.common.retry(async () => {
            const url = this.ctx.systemApi.getUrl(process.env.BUILD_ISEDM ? 'getColleagueList' : 'getLxColleagueList');
            const res = await this.ctx.http.get(url, {}, {
                contentType: 'json',
            });
            return res.data;
        });
        if (success && data && data.success && data.data) {
            const items = data.data;
            const currentEmail = (_a = this.ctx.systemApi.getCurrentUser()) === null || _a === void 0 ? void 0 : _a.id;
            return items
                .map((sd) => ({
                email: sd.email,
                id: sd.accId,
                nickName: sd.nickname,
            }))
                .filter((item) => item.email !== currentEmail);
        }
        console.error('[contact_college] getColleagueList error', error);
        return Promise.reject(new Error('getColleagueList error'));
    }
    async getResignList() {
        const { success, data, error } = await this.common.retry(async () => {
            const url = this.ctx.systemApi.getUrl('getResignList');
            const res = await this.ctx.http.post(url, {}, {
                contentType: 'json',
            });
            return res.data;
        });
        if (success && data && data.code === 200 && data.data) {
            const items = data.data;
            return items;
        }
        console.error('[contact_college] getResignList error', error);
        return Promise.reject(new Error('getResignList error'));
    }
    async getColleagueFolderList(colleagueAccId, subEmails) {
        const { success, data, error } = await this.common.retry(async () => {
            const url = this.ctx.http.buildUrl(this.ctx.systemApi.getUrl('getColleagueAllFolderList'), {
                colleagueAccId,
            });
            const res = await this.ctx.http.post(url, {
                order: 'custom_virtual',
                subEmails,
            }, {
                contentType: 'json',
            });
            return res.data;
        });
        if (success && data && data.success && data.data) {
            const entityMailBoxMap = this.common.mappingToMailBoxEntry(data.data);
            const result = {};
            Object.keys(entityMailBoxMap).forEach(_account => {
                const entityMailBox = entityMailBoxMap[_account];
                if (entityMailBox) {
                    result[_account] = this.common.handleEntityMailBox2MailBoxModel(entityMailBox);
                }
            });
            return result;
        }
        console.error('[contact_college] getColleagueFolderList error', error);
        return null;
    }
    async fetchNewEnterpriseAll(params) {
        var _a;
        const { domain, _account, enterpriseId: _enterpriseId, currentCompany } = params;
        try {
            const url = this.ctx.systemApi.getUrl('getEnterpriseContactsAll');
            const res = await this.ctx.http.get(url, { domain }, { _account });
            const data = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
            if (!data) {
                console.warn('[ContactServerEnterprise] fetchNewEnterpriseAll: empty data');
                return null;
            }
            const enterpriseId = _enterpriseId || data.orgId;
            const root = createEnterpriseRootUnit(enterpriseId, currentCompany);
            let serverOrgs = Array.isArray(data.units) ? data.units : [];
            if (serverOrgs.length === 0) {
                serverOrgs = [root];
            }
            const lastUpdateTime = Date.now();
            const orgs = transformNewUnitList(serverOrgs, { enterpriseId, lastUpdateTime });
            this.buildIdPathMap(orgs);
            let contacts = [];
            let contactItems = [];
            let orgContacts = [];
            if (data.hasAllContacts === 1 && Array.isArray(data.contacts) && data.contacts.length > 0) {
                const aggregated = aggregateNewContacts(data.contacts);
                const result = transformNewContactList(aggregated, { enterpriseId, lastUpdateTime, idPathMap: this.idPathMap });
                contacts = result.contacts;
                contactItems = result.contactItems;
                orgContacts = result.orgContacts;
            }
            return { hasAllContacts: data.hasAllContacts, orgs, contacts, contactItems, orgContacts, lastUpdateTime, serverLastUpdateTime: data.lastUpdateTime || 0 };
        }
        catch (ex) {
            console.error('[ContactServerEnterprise] fetchNewEnterpriseAll failed', ex);
            return null;
        }
    }
    async fetchEnterpriseUnitContacts(qyIds, orgIds, domain, enterpriseId, _account) {
        try {
            const url = this.ctx.systemApi.getUrl('getEnterpriseUnitContacts');
            const { data } = await this.ctx.http.post(url, { unitIds: qyIds, domain, orgId: enterpriseId }, { _account, contentType: 'json' });
            const body = data === null || data === void 0 ? void 0 : data.data;
            if (!body || !Array.isArray(body.contacts)) {
                return null;
            }
            body.contacts.forEach(item => {
                if (!item.orgId) {
                    item.orgId = enterpriseId;
                }
            });
            const lastUpdateTime = Date.now();
            const targetOrgIds = new Set(orgIds);
            const aggregated = aggregateNewContacts(body.contacts);
            const result = transformNewContactListForOrgIds(aggregated, targetOrgIds, { enterpriseId, lastUpdateTime, idPathMap: this.idPathMap });
            return result;
        }
        catch (ex) {
            console.error('[ContactServerEnterprise] fetchEnterpriseUnitContacts failed', ex);
            return null;
        }
    }
    buildIdPathMap(orgs) {
        const orgMap = new Map();
        orgs.forEach(org => orgMap.set(org.id, org));
        for (const org of orgs) {
            if (org.type === 99)
                continue;
            const path = [];
            const visited = new Set();
            let current = org;
            while (current && current.type !== 99 && !visited.has(current.id)) {
                visited.add(current.id);
                path.push(current.orgName);
                current = orgMap.get(current.parent);
            }
            path.reverse();
            this.idPathMap.set(org.id, path);
        }
    }
}
